import type { OperatingSystemProxyTicket } from "@/lib/operating-systems";

const TICKET_STORAGE_PREFIX = "applyt:os-runtime-ticket:v1";

type TicketStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function operatingSystemRuntimeTicketScope(runtimeVersion: number | null | undefined): string {
  return Number.isSafeInteger(runtimeVersion) && (runtimeVersion as number) >= 0
    ? `v${runtimeVersion}`
    : "unknown";
}

function ticketStorageKey(operatingSystemId: string, runtimeVersion: number | null | undefined): string {
  return `${TICKET_STORAGE_PREFIX}:${operatingSystemId}:${operatingSystemRuntimeTicketScope(runtimeVersion)}`;
}

export function readStoredOperatingSystemProxyTicket(
  operatingSystemId: string,
  storage: TicketStorage | null,
  nowMs = Date.now(),
  minimumRemainingMs = 30_000,
  runtimeVersion: number | null | undefined = null,
): OperatingSystemProxyTicket | null {
  if (!operatingSystemId || !storage) return null;
  try {
    const storageKey = ticketStorageKey(operatingSystemId, runtimeVersion);
    const value = JSON.parse(storage.getItem(storageKey) || "null") as Partial<OperatingSystemProxyTicket> | null;
    const expiresAtMs = typeof value?.expiresAt === "string" ? new Date(value.expiresAt).getTime() : Number.NaN;
    if (
      typeof value?.proxyUrl !== "string"
      || !value.proxyUrl
      || !Number.isFinite(expiresAtMs)
      || expiresAtMs <= nowMs + minimumRemainingMs
    ) {
      storage.removeItem(storageKey);
      return null;
    }
    return { proxyUrl: value.proxyUrl, expiresAt: value.expiresAt as string };
  } catch {
    storage.removeItem(ticketStorageKey(operatingSystemId, runtimeVersion));
    return null;
  }
}

export function storeOperatingSystemProxyTicket(
  operatingSystemId: string,
  ticket: OperatingSystemProxyTicket,
  storage: TicketStorage | null,
  runtimeVersion: number | null | undefined = null,
): void {
  if (!operatingSystemId || !storage) return;
  try {
    storage.setItem(ticketStorageKey(operatingSystemId, runtimeVersion), JSON.stringify(ticket));
  } catch {
    // Ticket reuse is an optimization; opening still works without storage.
  }
}

export function removeStoredOperatingSystemProxyTicket(
  operatingSystemId: string,
  storage: TicketStorage | null,
  runtimeVersion: number | null | undefined = null,
): void {
  if (!operatingSystemId || !storage) return;
  try {
    storage.removeItem(ticketStorageKey(operatingSystemId, runtimeVersion));
  } catch {
    // Ignore unavailable browser storage.
  }
}

export type ScopedTicketState = {
  operatingSystemId: string;
  runtimeVersion: number | null;
  ticket: OperatingSystemProxyTicket | null;
  loading: boolean;
  error: string | null;
};

export function scopedTicketForOperatingSystem(
  state: ScopedTicketState | null,
  operatingSystemId: string | null,
  runtimeVersion: number | null,
): ScopedTicketState | null {
  return state?.operatingSystemId === operatingSystemId && state.runtimeVersion === runtimeVersion
    ? state
    : null;
}

export function beginScopedTicketRefresh(
  state: ScopedTicketState | null,
  operatingSystemId: string,
  runtimeVersion: number | null,
): ScopedTicketState {
  return {
    operatingSystemId,
    runtimeVersion,
    ticket: state?.operatingSystemId === operatingSystemId && state.runtimeVersion === runtimeVersion
      ? state.ticket
      : null,
    loading: true,
    error: null,
  };
}

export function failScopedTicketRefresh(
  state: ScopedTicketState | null,
  operatingSystemId: string,
  runtimeVersion: number | null,
  error: string,
): ScopedTicketState {
  return {
    operatingSystemId,
    runtimeVersion,
    ticket: state?.operatingSystemId === operatingSystemId && state.runtimeVersion === runtimeVersion
      ? state.ticket
      : null,
    loading: false,
    error,
  };
}

export function renewScopedTicketIfCurrent(
  state: ScopedTicketState | null,
  operatingSystemId: string,
  runtimeVersion: number | null,
  expectedProxyUrl: string,
  expiresAt: string,
): ScopedTicketState | null {
  if (
    state?.operatingSystemId !== operatingSystemId
    || state.runtimeVersion !== runtimeVersion
    || state.ticket?.proxyUrl !== expectedProxyUrl
  ) {
    return null;
  }

  return {
    ...state,
    ticket: { proxyUrl: expectedProxyUrl, expiresAt },
    error: null,
  };
}

export function proxyTicketRenewalUrl(proxyUrl: string): string {
  const baseUrl = proxyUrl.endsWith("/") ? proxyUrl : `${proxyUrl}/`;
  return new URL("__applyt_ticket_renew", baseUrl).toString();
}

export function proxyTicketNeedsReplacement(
  responseStatus: number | null,
  expiresAt: string,
  nowMs = Date.now(),
): boolean {
  const expiresAtMs = new Date(expiresAt).getTime();
  return (
    responseStatus === 401
    || responseStatus === 404
    || responseStatus === 410
    || !Number.isFinite(expiresAtMs)
    || expiresAtMs <= nowMs
  );
}

export class OperatingSystemTicketRequestRegistry {
  private readonly requests = new Map<string, Promise<OperatingSystemProxyTicket | null>>();

  run(
    operatingSystemId: string,
    runtimeVersion: number | null,
    createRequest: () => Promise<OperatingSystemProxyTicket | null>,
  ): Promise<OperatingSystemProxyTicket | null> {
    const requestKey = `${operatingSystemId}:${operatingSystemRuntimeTicketScope(runtimeVersion)}`;
    const existing = this.requests.get(requestKey);
    if (existing) return existing;

    const request = createRequest();
    this.requests.set(requestKey, request);
    const clear = () => {
      if (this.requests.get(requestKey) === request) {
        this.requests.delete(requestKey);
      }
    };
    request.then(clear, clear);
    return request;
  }
}
